/**
 * INCSUBPoolverification.jsx
 *
 * A React component responsible for displaying pool verification details, 
 * including search, filtering, and pagination. It interacts with the Redux store
 * to manage state related to pool verification lists, types, statuses, and other metadata.
 * It also provides navigation to the individual pool verification details page.
 *
 * This component fetches pool verification data from an API and allows the user
 * to search and filter the list of pools based on type and status.
 *
 * @component
 * @example
 * // Usage of INCSUBPoolverification component
 * <INCSUBPoolverification />
 *
 * @author Govind Dindewad
 */

import React, { useEffect, useState } from 'react';
import './poolVerification.css';
import INCSUBSearchbar from '../common/SearchBar/INCSUBSearchbar';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { getPoolVerificationList } from '../../services/poolVerification';
import { setPoolVerificationList, setSelectedVerificationPool } from '../../features/poolVerifivation/poolVerificationSlice';
import { toast } from 'react-toastify';
import INCSUBFilterList from '../common/filter/FilterList';
import INCSUBPoolVerificationDetails from '../../pages/pool Verification/poolVerificationDetails';
import { setPoolVerificationPath } from '../../redux/commonSlice';
import ReactPaginate from 'react-paginate';
import INCSUBButton from '../common/Button/INCSUBButton';
import INCSUBPaymentVerification from '../../pages/userPayments/paymentVerification';

/**
 * INCSUBPoolverification Component
 *
 * @param {Object} props - React props
 * @returns {JSX.Element} Rendered pool verification UI with search, filters, and data tables
 * 
 * @author Govind Dindewad
 */
function INCSUBPoolverification() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [shimmer, setShimmer] = useState();
  const [pageNo, setPageNo] = useState(0);
  const [type, setType] = useState('All');
  const [status, setStatus] = useState('All');
  const [searchKey, setSearchKey] = useState('');
  const verificationList = useSelector((state) => state.poolVerification.poolVerificationList);
  const poolVerificationPath = useSelector((state) => state.common.poolVerificationPath);

  // Options for pool type and status filters
  const poolTypeList = [
    { id: 'All', name: 'All' },
    { id: 'Standard', name: 'Standard' },
    { id: 'Instant', name: 'Instant' },
    { id: 'Rental', name: 'Rental' },
  ];

  const poolStatusList = [
    { id: 'All', name: 'All' },
    { id: 'Pending', name: 'Pending' },
    // { id: 'Verified', name: 'Verified' },
    { id: 'Rejected', name: 'Rejected' },
  ];

  /**
   * Formats the provided date-time string into a human-readable date and time format.
   *
   * @param {string} dateTime - The date-time string to format.
   * @returns {string} The formatted date and time string.
   * 
   * @author Govind Dindewad
   */
  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    const formattedDate = date.toLocaleDateString('en-GB', options).replace(',', '');
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedTime = `${hours % 12 || 12}:${minutes} ${period}`;
    return `${formattedDate} ${formattedTime}`;
  };

  /**
   * Fetches pool verification data based on the current filter and pagination state.
   *
   * @param {Object} params - Filter and pagination parameters.
   * @param {string} params.searchKeyState - Search keyword.
   * @param {string} params.typeState - Pool type filter value.
   * @param {string} params.statusState - Pool status filter value.
   * @param {number} params.pageState - The current page for pagination.
   * 
   * @author Govind Dindewad
   */
  const fetchVerificationData = async ({
    searchKeyState = searchKey,
    typeState = type,
    statusState = status,
    pageState = pageNo,
  } = {}) => {
    setShimmer(true);
    try {
      const data = await getPoolVerificationList({ key: searchKeyState, type: typeState, status: statusState, page: pageState });
      if (data.status === 200) {
        dispatch(setPoolVerificationList(data.data.data));
      } else if (data.status === 401) {
        navigate('/login');
      } else if (data.status === 301) {
        toast.error("Network Error");
      } else if (data.status === 500) {
        toast.error("Internal server error");
      } else if (![200, 401].includes(data.status)) {
        toast.error(data.data.message);
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setShimmer(false);
    }
  };

  useEffect(() => {
    if (poolVerificationPath !== '/poolVerificationDetails') {
      fetchVerificationData();
    }
  }, []);

  /**
   * Handles search input change and updates the search key.
   *
   * @param {string} search - The search keyword.
   * 
   * @author Govind Dindewad
   */
  const handleSearch = (search) => {
    setSearchKey((prevSearch) => {
      const newSearch = search;
      fetchVerificationData({ searchKeyState: newSearch });
      return newSearch;
    });
  };

  /**
   * Handles the change in the pool status filter.
   *
   * @param {Object} status - The selected status option.
   * 
   * @author Govind Dindewad
   */
  const handleStatusChange = (status) => {
    setStatus((prevStatus) => {
      const newStatus = status.name;
      fetchVerificationData({ statusState: newStatus });
      return newStatus;
    });
  };

  /**
   * Handles the change in the pool type filter.
   *
   * @param {Object} type - The selected type option.
   * 
   * @author Govind Dindewad
   */
  const handleTypeChange = (type) => {
    setType((prevType) => {
      const newType = type.name;
      fetchVerificationData({ typeState: newType });
      return newType;
    });
  };

  const [currentPage, setCurrentPage] = useState(0);

  /**
   * PaginatedItems Component
   *
   * Renders the pagination UI for pool verification data and handles page changes.
   * @returns {JSX.Element} Pagination component.
   * 
   * @author Govind Dindewad
   */
  function PaginatedItems() {
    const [itemOffset, setItemOffset] = useState(2);
    const endOffset = itemOffset;
    const pageCount = verificationList ? verificationList.totalPages : 0;

    const handlePageClick = (event) => {
      setPageNo(event.selected);
      fetchVerificationData({ pageState: event.selected });
      setCurrentPage(event.selected);
      setItemOffset(event.selected);
    };

    return (
      <div className="INCSUBComplaints_list_section_container">
        {!shimmer && verificationList && verificationList.verificationRequestList.length > 0 && pageCount > 1 ? (
          <ReactPaginate
            breakLabel="..."
            nextLabel="next >"
            onPageChange={handlePageClick}
            pageRangeDisplayed={5}
            pageCount={pageCount}
            previousLabel="< previous"
            renderOnZeroPageCount={null}
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            containerClassName="pagination"
            activeClassName="active"
            forcePage={currentPage}
          />
        ) : null}
      </div>
    );
  }
const handlePaymentRequests=()=>{
  console.log("ehllo")
  dispatch(setPoolVerificationPath("/paymentVerification"))

}
  return (
    <div className="INCSUBVerificationpolls_ctn">
      {poolVerificationPath === '/poolverification' ? (
        <div className="INCSUBVerificationpolls_ctn_searchbar_hold">
          <div className="INCSUBPools_Search_filters_container">
            <INCSUBSearchbar source={''} placeholder={'Search'} param={{ pageNumber: pageNo, func: handleSearch }} />
            <INCSUBFilterList list={poolTypeList} value={type} func={handleTypeChange} type={'type'} />
            <INCSUBFilterList list={poolStatusList} value={status} func={handleStatusChange} type={'Status'} />
          </div>
        </div>
      ) : null}
      {poolVerificationPath === '/poolverification' ? (
        verificationList && verificationList.verificationRequestList.length > 0 ? (
          <>
            <div className="INCSUBUser_userlist_section">
              <div className="INCSUBUser_userlist_section_table_conatiner">
                <table className="INCSUBuser_table">
                  <thead className="INCSUBuser_table_head">
                    <tr>
                      <th>#</th>
                      <th>Type</th>
                      <th>Pool Name</th>
                      <th>Host</th>
                      <th>Submitted Date</th>
                      <th>Validity</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Handled By</th>
                    </tr>
                  </thead>
                  <tbody className="INCSUBuser_table_body">
                    {verificationList &&
                      verificationList.verificationRequestList.length > 0 &&
                      verificationList.verificationRequestList.map((pool, index) => (
                        <tr
                          style={{ background: index % 2 !== 0 ? 'rgba(244, 248, 255, 1)' : '#fff' }}
                          onClick={() => {
                            dispatch(setSelectedVerificationPool(pool));
                            dispatch(setPoolVerificationPath('/poolVerificationDetails'));
                          }}
                        >
                          <td>{index + 1}</td>
                          <td className={`INCSUBVerificationpools_row_pooltype ${pool?.poolType}`}>{pool?.poolType}</td>
                          <td style={{ fontSize: '16PX' }}>{pool?.poolName}</td>
                          <td className="INCSUBVerificationpools_row_host">
                            <div className="INCSUBVerificationpools_row_host_img">
                              <img
                                className="INCSUB_UserImgTag"
                                src={pool && pool.hostImage ? pool.hostImage : '/assets/icons/common/defaultImage.png'}
                                alt={`${pool ? pool.verifiedByImage : 'default'} img`}
                              />
                            </div>
                            <div className="INCSUBVerificationpools_row_host_name">{pool?.hostName}</div>
                          </td>
                          <td>{pool?.verificationCreatedAt ? formatDateTime(pool?.verificationCreatedAt) : null}</td>
                          <td>{`${pool?.poolValidity} days`}</td>
                          <td>{`₹ ${pool?.poolAmount}`}</td>
                          <td>
                            <div className={`INCSUBVerificationpools_row_status_div ${pool?.verificationStatus}`}>
                              {pool?.verificationStatus}
                            </div>
                          </td>
                          <td className="INCSUBVerificationpools_row_handledBy">
                            <div className="INCSUBVerificationpools_row_handledBy_name_date_img">
                              {pool?.verifiedByName ? (
                                <div className="INCSUBVerificationpools_row_host_img">
                                  <img
                                    className="INCSUB_UserImgTag"
                                    src={pool ? pool.verifiedByImage : '/assets/icons/common/defaultImage.png'}
                                    alt={`${'default'} img`}
                                  />
                                </div>
                              ) : null}

                              <div className="INCSUBVerificationpools_row_handledBy_name_time_date">
                                <div className="INCSUBVerificationpools_row_handledBy_name">{pool?.verifiedByName}</div>
                                <div className="INCSUBVerificationpools_row_handledBy_time_date">
                                  {pool?.verificationDate ? formatDateTime(pool?.verificationDate) : null}
                                </div>
                              </div>
                            </div>
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ cursor: 'pointer' }}
                            >
                              <path
                                d="M9 18L15 12L9 6"
                                stroke="black"
                                stroke-opacity="0.6"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
            <PaginatedItems />
          </>
        ) : (
          <div className="INCSUBPoolverification_nodata_container">
            <div className="INCSUBPoolverification_nodata">No Verification Data</div>
          </div>
        )
      ) : null}
      {poolVerificationPath === '/poolVerificationDetails' ? <INCSUBPoolVerificationDetails /> : null}
    </div>
  );
}

export default INCSUBPoolverification;
